import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatPhoneWithCountry } from "../utils/countries";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Search,
  Building2,
  Home,
  Phone,
  Mail,
  MapPin,
  TrendingUp,
  Trash2,
  Users,
  Download,
} from "lucide-react";
import { downloadCSV, csvCurrency } from "../utils/csv";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  LEAD: "Lead",
  ACTIVO: "Activo",
  IMPAGO: "Impago",
  BAJA: "Baja",
  // Legacy - mantener para compatibilidad
  INACTIVO: "Inactivo",
  PENDIENTE: "Pendiente",
};

const statusColors = {
  LEAD: "bg-[#03BEE4]/20 text-[#03BEE4] border-[#03BEE4]/30",
  ACTIVO: "bg-[#42E695]/20 text-[#42E695] border-[#42E695]/30",
  IMPAGO: "bg-red-500/20 text-red-400 border-red-500/30",
  BAJA: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  // Legacy - mantener para compatibilidad
  INACTIVO: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  PENDIENTE: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
};

const CustomersPage = () => {
  const navigate = useNavigate();
  const { user, canDelete, canExport } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const fetchCustomers = useCallback(async () => {
    try {
      let url = `${API_URL}/api/customers`;
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("customer_type", typeFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchTerm) params.append("search", searchTerm);
      if (params.toString()) url += `?${params.toString()}`;

      const customersRes = await axios.get(url);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, searchTerm]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/customers/${customerToDelete.id}`);
      toast.success("Cliente eliminado correctamente");
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      fetchCustomers();
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar cliente");
    }
  };

  const openDeleteDialog = (e, customer) => {
    e.stopPropagation();
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  const getCustomerDisplayName = (customer) => {
    if (customer.customer_type === "EMPRESA") {
      return customer.razon_social || "";
    }
    return `${customer.nombre || ""} ${customer.apellidos || ""}`.trim();
  };

  const handleExportCSV = () => {
    if (customers.length === 0) {
      toast.error("No hay clientes para exportar");
      return;
    }
    const columns = [
      { key: "customer_id", label: "Referencia" },
      { key: "name", label: "Nombre", format: (_, row) => getCustomerDisplayName(row) },
      { key: "customer_type", label: "Tipo" },
      { key: "status", label: "Estado", format: (v) => statusLabels[v] || v || "" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Teléfono" },
      { key: "ciudad", label: "Ciudad" },
      { key: "mrr", label: "MRR", format: csvCurrency },
      { key: "active_services", label: "Servicios" },
    ];
    downloadCSV("clientes", columns, customers);
    toast.success(`${customers.length} clientes exportados a CSV`);
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <Users className="h-8 w-8 text-[#03BEE4]" />
            Clientes
          </h1>
          <p className="text-slate-400 mt-1">
            Gestión de clientes empresariales y residenciales
          </p>
        </div>

        {/* Botón eliminado: los clientes se crean desde Pipeline */}
        {canExport("customers") && (
          <Button
            onClick={handleExportCSV}
            variant="outline"
            className="border-[#42E695] text-[#42E695] hover:bg-[#42E695]/20"
            data-testid="export-customers-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre, ID o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-customers-input"
            className="pl-10 bg-[#0A0A35] border-[#1E1E5F] text-white placeholder:text-slate-500"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-[#0A0A35] border-[#1E1E5F] text-white">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
            <SelectItem value="all" className="text-white">Todos los tipos</SelectItem>
            <SelectItem value="EMPRESA" className="text-white">Empresas</SelectItem>
            <SelectItem value="RESIDENCIAL" className="text-white">Residencial</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-[#0A0A35] border-[#1E1E5F] text-white">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
            <SelectItem value="all" className="text-white">Todos los estados</SelectItem>
            {Object.entries(statusLabels).map(([value, label]) => (
              <SelectItem key={value} value={value} className="text-white">
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Customers Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
        </div>
      ) : customers.length === 0 ? (
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-slate-500 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No hay clientes</h3>
            <p className="text-slate-400 text-center">
              {searchTerm
                ? "No se encontraron clientes con esos criterios"
                : "Los clientes se crean desde Pipeline al convertir oportunidades"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((customer) => (
            <Card
              key={customer.id}
              className="bg-[#0A0A35] border-[#1E1E5F] hover:border-[#03BEE4]/50 transition-all cursor-pointer group"
              onClick={() => navigate(`/customers/${customer.id}`)}
              data-testid={`customer-card-${customer.customer_id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      customer.customer_type === "EMPRESA"
                        ? "bg-[#03BEE4]/20"
                        : "bg-purple-500/20"
                    }`}>
                      {customer.customer_type === "EMPRESA" ? (
                        <Building2 className="h-5 w-5 text-[#03BEE4]" />
                      ) : (
                        <Home className="h-5 w-5 text-purple-400" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-white text-base group-hover:text-[#03BEE4] transition-colors">
                        {getCustomerDisplayName(customer)}
                      </CardTitle>
                      <p className="text-sm text-[#42E695] font-mono font-bold">
                        {customer.customer_id}
                      </p>
                    </div>
                  </div>
                  <Badge className={statusColors[customer.status]}>
                    {statusLabels[customer.status]}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {customer.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Mail className="h-4 w-4" />
                    <span className="truncate">{customer.email}</span>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Phone className="h-4 w-4" />
                    <span>{formatPhoneWithCountry(customer.phone, customer.phone_country)}</span>
                  </div>
                )}
                {(customer.ciudad || customer.direccion_fiscal) && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{customer.ciudad || customer.direccion_fiscal}</span>
                  </div>
                )}

                <div className="pt-3 border-t border-[#1E1E5F] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-[#42E695]" />
                    <span className="text-sm text-white font-medium">
                      {formatCurrency(customer.mrr)}/mes
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">
                      {customer.active_services} servicios
                    </span>
                    {canDelete("customers") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => openDeleteDialog(e, customer)}
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                        data-testid={`delete-customer-${customer.customer_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A35] border-[#1E1E5F]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esta acción eliminará permanentemente al cliente <span className="text-[#42E695] font-semibold">{customerToDelete?.customer_id}</span> y todos sus datos asociados (ubicaciones, servicios, oportunidades, provisiones, incidencias y solicitudes).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#1E1E5F] text-slate-300 hover:bg-[#1E1E5F]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCustomer}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomersPage;
